import { omit } from 'lodash-es';

import { ISanityBlockContent, ISanityTextNode } from '@rbi-ctg/menu';
import {
  IOfferGroupFragment as GatewayOfferGroup,
  IAppliedOffer,
  ILoyaltyEngineRewardsQuery,
  IOffersFragment,
  LoyaltyOfferType,
  Maybe,
} from 'generated/graphql-gateway';
import {
  ICmsConfigRewardsQuery,
  IConfigOfferFragment,
  IFeatureLoyaltyOfferGroupsQuery,
  ILoyaltyRewardsByEngineIdQuery,
  IOfferGroupFragment,
  ISystemwideOffersFragment,
  ISystemwideTierGroupFragment,
} from 'generated/sanity-graphql';
import { getOfferServiceMode } from 'pages/loyalty/loyalty-offers/loyalty-service-modes';
import { OffersServiceMode } from 'state/loyalty/offers/types';

// TODO: remove if not used
export type SelectedRewardTierIdType = string | null | undefined;
export interface ILoyaltyCategoryOption {
  name: ISanityTextNode;
  description: ISanityBlockContent;
  points: number;
  _id: string;
}
export interface ILoyaltyCategory {
  _id: string;
  name: ISanityTextNode;
  description: ISanityBlockContent;
  options: Array<ILoyaltyCategoryOption>;
}

// TODO: remove if not used
export interface ILoyaltyCategories {
  allLoyaltyConfigurations: {
    categories: ILoyaltyCategory;
  };
  errors?: Error[];
}

// TODO: remove if not used
export interface ILoyaltyTier {
  name: ISanityTextNode;
  description: ISanityBlockContent;
  points: number;
  _id: string;
}

// TODO: remove if not used
export interface ILoyaltyTiers {
  allLoyaltyConfigurations: {
    bankMyRewardTier: ILoyaltyTier;
  };
  errors?: Error[];
}

export type LoyaltyRewards =
  | ILoyaltyRewardsByEngineIdQuery['allRewards']
  | ICmsConfigRewardsQuery['allConfigRewards'];

export type LoyaltyReward = LoyaltyRewards[0];

export type LoyaltyEngineRewards = ILoyaltyEngineRewardsQuery['loyaltyRewardsV2'];

export type LoyaltyEngineReward = NonNullable<LoyaltyEngineRewards>[0];

export type LoyaltyOffer = (ISystemwideOffersFragment | IConfigOfferFragment) &
  Pick<IOffersFragment, 'isStackable' | 'metadata' | 'expirationDate'> & {
    offerServiceMode?: OffersServiceMode;
    serviceModes?: any;
    errors?: any;
    inlineAlertMessage?: any;
    endDate?: string;
  };

export type LoyaltyOfferGroup = IFeatureLoyaltyOfferGroupsQuery['allOfferGroups'] &
  Omit<IOfferGroupFragment, 'offers'> & {
    offers: ReadonlyArray<
      | ({ readonly __typename?: 'SystemwideOffer' } & ISystemwideOffersFragment & {
            endDate?: string;
          })
      | null
    >;
  };

export type LoyaltySystemwideTierGroup = ISystemwideTierGroupFragment;

export enum IncentiveSource {
  SUPPORT = 'SUPPORT',
  PROMOTION_CODE = 'PROMOTION_CODE',
}

export type LoyaltyAppliedOffer = IAppliedOffer & {
  isStackable?: Maybe<boolean>;
  cmsId?: Maybe<string>;
};

export function isReward(incentive: { __typename?: string }): incentive is LoyaltyReward {
  return incentive.__typename === 'Reward' || incentive.__typename === 'ConfigReward';
}

export function isLoyaltyOffer(incentive: any): incentive is LoyaltyOffer {
  return incentive?.__typename === 'SystemwideOffer' || incentive?.__typename === 'ConfigOffer';
}

// Offer Incentives must have an _id to be valid
export function isValidOfferIncentive(obj: any): obj is { _id: string } {
  return !!obj?._id;
}

export type IEngineRewardsMap = {
  [sanityId: string]: LoyaltyEngineReward;
};
export type IPersonalizedRewardMap = {
  [rewardId: string]: LoyaltyEngineReward;
};
export type ISanityRewardsMap = Record<LoyaltyReward['_id'], LoyaltyReward>;

export type TLoyaltyCooldown = { isActive: false } | { isActive: true; endsAt: Date };
export type SanityId = string & { __brand: 'SanityId' };
export type LoyaltyEngineId = string & { __brand: 'LoyaltyEngineId' };

// -----------------------------------------------------------------------------
// SANITY TYPES
// Prefixed with "Sanity"
// -----------------------------------------------------------------------------

enum SanityTypename {
  SYSTEMWIDE_OFFER = 'SystemwideOffer',
  CONFIG_OFFER = 'ConfigOffer',
  OFFER_GROUP = 'OfferGroup',
  SYSTEMWIDE_TIER_GROUP = 'SystemwideTierGroup',
}

// Sanity Systemwide Offer
export type SanitySystemwideOffer = Omit<ISystemwideOffersFragment, '_id' | 'loyaltyEngineId'> & {
  _id: SanityId;
  loyaltyEngineId: LoyaltyEngineId;
  isTieredOffer?: boolean;
};
export function isSanitySystemwideOffer(o: any): o is SanitySystemwideOffer {
  return o?.__typename === SanityTypename.SYSTEMWIDE_OFFER;
}

// Sanity Config Offer
export type SanityConfigOffer = Omit<IConfigOfferFragment, '_id' | 'loyaltyEngineId'> & {
  _id: SanityId;
  loyaltyEngineId: LoyaltyEngineId;
};
export function isSanityConfigOffer(o: any): o is SanityConfigOffer {
  return o?.__typename === SanityTypename.CONFIG_OFFER;
}

// Sanity Offer (Systemwide or Config)
export type SanityOffer = SanitySystemwideOffer | SanityConfigOffer;
export function isSanityOffer(o: any): o is SanityOffer {
  return isSanitySystemwideOffer(o) || isSanityConfigOffer(o);
}

// Sanity Offer Group
export type SanityOfferGroup = Omit<IOfferGroupFragment, '_id' | 'loyaltyEngineId' | 'offers'> & {
  _id: SanityId;
  loyaltyEngineId: LoyaltyEngineId;
  offers: SanitySystemwideOffer[];
};
export function isSanityOfferGroup(og: any): og is SanityOfferGroup {
  return og?.__typename === SanityTypename.OFFER_GROUP;
}

// Sanity Systemwide Tier Group
export type SanitySystemwideTierGroup = Omit<ISystemwideTierGroupFragment, '_id' | 'offers'> & {
  _id: SanityId;
  offers: SanitySystemwideOffer[];
};
export function isSanitySystemwideTierGroup(tg: any): tg is SanitySystemwideTierGroup {
  return tg?.__typename === SanityTypename.SYSTEMWIDE_TIER_GROUP;
}

// Sanity Offer List Item + Tier Groups
export type SanityOfferListItemWithExtras =
  | SanitySystemwideOffer
  | SanityOfferGroup
  | SanitySystemwideTierGroup;

export function isSanityOfferListItemWithExtras(i: any): i is SanityOfferListItemWithExtras {
  return isSanitySystemwideOffer(i) || isSanityOfferGroup(i) || isSanitySystemwideTierGroup(i);
}

// Sanity Visible Offer List Item (excludes Tier Groups because they are not
// displayed in the UI but one of the child systemwide offers could be)
export type SanityOfferListItem = Exclude<SanityOfferListItemWithExtras, SanitySystemwideTierGroup>;
export function isSanityOfferListItem(i: any): i is SanityOfferListItem {
  return isSanitySystemwideOffer(i) || isSanityOfferGroup(i);
}

// -----------------------------------------------------------------------------
// LOYALTY ENGINE TYPES
// Prefixed with "Engine"
// -----------------------------------------------------------------------------

enum EngineTypename {
  LOYALTY_OFFER = 'LoyaltyOffer',
  LOYALTY_OFFER_GROUP = 'LoyaltyOfferGroup',
}

// Engine Systemwide Offer
export type EngineSystemwideOffer = Omit<
  IOffersFragment,
  'id' | 'type' | 'loyaltyId' | 'configId'
> & {
  id: LoyaltyEngineId;
  type: LoyaltyOfferType.GLOBAL;
};
export function isEngineSystemwideOffer(o: any): o is EngineSystemwideOffer {
  return o?.type === LoyaltyOfferType.GLOBAL && o?.__typename === EngineTypename.LOYALTY_OFFER;
}

// Engine Personalized Offer
export type EnginePersonalizedOffer = Omit<IOffersFragment, 'id' | 'type' | 'configId'> & {
  id: LoyaltyEngineId;
  type: LoyaltyOfferType.PERSONALIZED;
  configId: LoyaltyEngineId;
};
export function isEnginePersonalizedOffer(o: any): o is EnginePersonalizedOffer {
  return (
    o?.type === LoyaltyOfferType.PERSONALIZED && o?.__typename === EngineTypename.LOYALTY_OFFER
  );
}

// Engine Offer (Systemwide or Personalized)
export type EngineOffer = EngineSystemwideOffer | EnginePersonalizedOffer;

export type EngineOfferGroup = Omit<GatewayOfferGroup, 'id' | 'cmsId' | 'offers'> & {
  id: LoyaltyEngineId;
  cmsId: SanityId;
  offers: EngineSystemwideOffer[];
};
export function isEngineOfferGroup(og: any): og is EngineOfferGroup {
  return og?.__typename === EngineTypename.LOYALTY_OFFER_GROUP;
}

export type EngineOfferListItem = EngineOffer | EngineOfferGroup;
export type EngineNonPersonalizedOfferListItem = EngineSystemwideOffer | EngineOfferGroup;
export function isEngineNonPersonalizedOfferListItem(
  i: any
): i is EngineNonPersonalizedOfferListItem {
  return isEngineSystemwideOffer(i) || isEngineOfferGroup(i);
}

// -----------------------------------------------------------------------------
// APP TYPES
// Types that combine Sanity and Engine data, prefixed with "App"
// -----------------------------------------------------------------------------

enum AppType {
  SYSTEMWIDE_OFFER = 'AppSystemwideOffer',
  PERSONALIZED_OFFER = 'AppPersonalizedOffer',
  OFFER_GROUP = 'AppOfferGroup',
}

// App Systemwide Offer
export function buildAppSystemwideOffer(
  sanityOffer: SanitySystemwideOffer,
  engineOffer: EngineSystemwideOffer
) {
  const sanity = omit(sanityOffer, ['redemptionType']);
  const engine = omit(engineOffer, ['id', '__typename', 'name', 'vendorConfigs']);
  return {
    ...sanity,
    ...engine,
    type: engineOffer.type!,
    loyaltyEngineId: engineOffer.id,
    cmsId: sanityOffer._id,
    endDate: engineOffer.endDate!,
    expirationDate: engineOffer.expirationDate,
    offerServiceMode: getOfferServiceMode(engineOffer?.serviceModes),
    __typename: sanity.__typename,
    __apptype: AppType.SYSTEMWIDE_OFFER as const,
    inlineAlertMessage: undefined as string | undefined,
    vendorConfigs: sanity.vendorConfigs,
  };
}
export type AppSystemwideOffer = ReturnType<typeof buildAppSystemwideOffer>;
export function isAppSystemwideOffer(o: any): o is AppSystemwideOffer {
  return o?.__apptype === AppType.SYSTEMWIDE_OFFER;
}

// App Personalized Offer
export function buildAppPersonalizedOffer(
  sanityOffer: SanityConfigOffer,
  engineOffer: EnginePersonalizedOffer
) {
  const sanity = omit(sanityOffer, ['redemptionType']);
  const engine = omit(engineOffer, ['id', '__typename', 'name', 'vendorConfigs']);
  return {
    ...sanity,
    ...engine,
    type: engineOffer.type!,
    loyaltyEngineId: engineOffer.id,
    configId: engineOffer.configId!,
    endDate: engineOffer.endDate!,
    expirationDate: engineOffer.expirationDate,
    cmsId: sanityOffer._id,
    offerServiceMode: getOfferServiceMode(engineOffer?.serviceModes),
    __typename: sanity.__typename,
    __apptype: AppType.PERSONALIZED_OFFER as const,
    inlineAlertMessage: undefined as string | undefined,
    vendorConfigs: sanity.vendorConfigs,
  };
}
export type AppPersonalizedOffer = ReturnType<typeof buildAppPersonalizedOffer>;
export function isAppPersonalizedOffer(o: any): o is AppPersonalizedOffer {
  return o?.__apptype === AppType.PERSONALIZED_OFFER;
}

// App Offer (Systemwide or Personalized)
export type AppOffer = AppSystemwideOffer | AppPersonalizedOffer;
export function isAppOffer(o: any): o is AppOffer {
  return isAppSystemwideOffer(o) || isAppPersonalizedOffer(o);
}

export type WithIncentives = Pick<AppOffer, 'incentives'>;
export type WithErrors = Pick<AppOffer, 'errors'>;

// App Offer Group
export function buildAppOfferGroup(
  sanityOfferGroup: SanityOfferGroup,
  engineOfferGroup: EngineOfferGroup,
  appOffers: AppOffer[]
) {
  const sanity = omit(sanityOfferGroup, ['offers']);
  const engine = omit(engineOfferGroup, ['id', '__typename', 'cmsId', 'offers']);
  return {
    ...sanity,
    ...engine,
    canOnlyChooseOne: !!sanityOfferGroup.canOnlyChooseOne,
    cmsId: sanityOfferGroup._id,
    offers: appOffers,
    __typename: sanity.__typename,
    __apptype: AppType.OFFER_GROUP as const,
  };
}
export type AppOfferGroup = ReturnType<typeof buildAppOfferGroup>;
export function isAppOfferGroup(og: any): og is AppOfferGroup {
  return og?.__apptype === AppType.OFFER_GROUP;
}

// App Offer List Item
export type AppOfferListItem = AppOffer | AppOfferGroup;

// -----------------------------------------------------------------------------
// COMPATIBILITY LAYER
// Functions to convert between legacy and new types
// -----------------------------------------------------------------------------
// ⚠️ TEMPORARY: These compatibility layer functions are transitional utilities.
// Prefer using the correct types (AppOffer, AppOfferGroup, etc.) directly.
// Only use these conversion functions when interfacing with legacy code that
// hasn't been migrated yet.
// -----------------------------------------------------------------------------

// Convert AppOffer to LoyaltyOffer
// ⚠️ TEMPORARY: Prefer using AppOffer directly instead of converting to LoyaltyOffer
export function appOfferToLoyaltyOffer(offer: AppOffer): LoyaltyOffer {
  return {
    ...omit(offer, ['cmsId', '__apptype']),
    _id: offer.cmsId,
    _type: isAppSystemwideOffer(offer) ? 'systemwideOffer' : 'configOffer',
  };
}

// Convert AppOfferListItem[] to LoyaltyOffer[]
// ⚠️ TEMPORARY: Prefer using AppOfferListItem[] directly instead of converting to LoyaltyOffer[]
export function appOferListItemsCompat(appOfferListItems: AppOfferListItem[]): LoyaltyOffer[] {
  return appOfferListItems.filter(isAppOffer).map(offer => appOfferToLoyaltyOffer(offer));
}

// Convert SanitySystemwideOffer to LoyaltyOffer
// ⚠️ TEMPORARY: Prefer using SanitySystemwideOffer directly instead of converting to LoyaltyOffer
export function sanitySystemwideOfferToLoyaltyOffer(
  offer: SanitySystemwideOffer
): LoyaltyOffer & { isTieredOffer?: boolean } {
  return {
    ...omit(offer, ['__apptype', '_type']),
    _id: offer._id,
    _type: SanityTypename.SYSTEMWIDE_OFFER,
    loyaltyEngineId: offer.loyaltyEngineId,
    isTieredOffer: offer.isTieredOffer,
  };
}
